import dayjs from "dayjs"

export interface DurationParts {
  startTime?: string | null
  endTime?: string | null
  hours?: number | null
  minutes?: number | null
}

/** Resolve an entry's duration from explicit hours/minutes, or from start/end times. */
const getHoursAndMinutes = (e: DurationParts): { hours: number; minutes: number } => {
  if (e.hours != null || e.minutes != null) {
    return { hours: e.hours ?? 0, minutes: e.minutes ?? 0 }
  }
  if (e.startTime && e.endTime) {
    const diff = Math.max(0, dayjs(e.endTime).diff(dayjs(e.startTime), "minute"))
    return { hours: Math.floor(diff / 60), minutes: diff % 60 }
  }
  return { hours: 0, minutes: 0 }
}

/** Duration expressed as decimal hours (e.g. 1h 30m → 1.5), for money math. */
export const getDecimalHours = (e: DurationParts): number => {
  const { hours, minutes } = getHoursAndMinutes(e)
  return hours + minutes / 60
}

/**
 * Locale-neutral duration in H:MM form, e.g. "1:30", "0:45", "12:05".
 * Colon format matches QuickBooks/timesheet convention and avoids the
 * decimal-separator and h/m-abbreviation ambiguity across locales.
 */
export const getDuration = (e: DurationParts): string => {
  const { hours, minutes } = getHoursAndMinutes(e)
  return `${hours}:${String(minutes).padStart(2, "0")}`
}

/** Whole minutes in the same H:MM form as `getDuration`. */
export const formatMinutes = (minutes: number): string =>
  `${Math.floor(minutes / 60)}:${String(Math.round(minutes % 60)).padStart(2, "0")}`

/**
 * Minutes from one clock time to a later one, or null while either is missing
 * or the end is not after the start.
 *
 * Takes what `<input type="time">` gives ("09:30") as well as what the API
 * sends back ("09:30:00"). The end is on the start's day: work across midnight
 * is two entries, which is what the API accepts.
 */
export const minutesBetween = (start: string, end: string): number | null => {
  const toMinutes = (time: string) => {
    const match = time.match(/^(\d{1,2}):(\d{2})/)
    return match ? Number(match[1]) * 60 + Number(match[2]) : null
  }
  const from = toMinutes(start)
  const to = toMinutes(end)
  if (from === null || to === null || to <= from) return null
  return to - from
}

/**
 * A clock time from the API ("14:30:00") the way a row shows it: "2:30 PM".
 *
 * Deliberately not locale-formatted: the card back already renders dates
 * through `Intl` with Trello's locale, but a span is read against the other
 * span above it, and a 12-hour clock is what the form's own `<input
 * type="time">` shows on most of the machines this runs on.
 */
export const formatClockTime = (time: string): string => {
  const match = time.match(/^(\d{1,2}):(\d{2})/)
  if (!match) return time
  const hours = Number(match[1])
  const suffix = hours < 12 ? "AM" : "PM"
  return `${hours % 12 || 12}:${match[2]} ${suffix}`
}

/** The span a pair of API times reads as, e.g. "9:00 AM – 10:30 AM". */
export const formatSpan = (
  start: string | null | undefined,
  end: string | null | undefined,
): string | null =>
  start && end ? `${formatClockTime(start)} – ${formatClockTime(end)}` : null

/** Whole minutes as decimal hours, matching how the API rounds an invoice line. */
export const minutesToDecimalHours = (minutes: number): number =>
  Math.round((minutes / 60) * 100) / 100

/**
 * A typed duration as whole minutes, or null if it can't be read.
 *
 * Deliberately forgiving about *how* the duration is written, because the
 * habits differ: "1:30" is the timesheet convention, "1.5" is what people who
 * bill in decimal hours type, and "1h 30m" is what everyone else reaches for. A
 * bare number is hours — the same reading QuickBooks gives a Qty — so "2" is
 * two hours, and minutes need their unit ("45m").
 */
export const parseDuration = (input: string): number | null => {
  const text = input.trim().toLowerCase()
  if (!text) return null

  const colon = text.match(/^(\d+):([0-5]?\d)$/)
  if (colon) return Number(colon[1]) * 60 + Number(colon[2])

  const hoursAndMinutes = text.match(/^(?:(\d+(?:\.\d+)?)\s*h(?:rs?|ours?)?)?\s*(?:(\d+)\s*m(?:ins?|inutes?)?)?$/)
  if (hoursAndMinutes && (hoursAndMinutes[1] || hoursAndMinutes[2])) {
    return Math.round(Number(hoursAndMinutes[1] ?? 0) * 60) + Number(hoursAndMinutes[2] ?? 0)
  }

  if (/^\d+(?:\.\d+)?$/.test(text)) return Math.round(Number(text) * 60)

  return null
}
